<script setup lang="ts">
    import { computed } from 'vue';
    import { RouterLink } from 'vue-router';
    import { type Issue, statusName } from './interfaces';
    import IssueSubject from './IssueSubject.vue';

    const { issue, isCreateNew, currentTab } = defineProps<{
        issue: Issue;
        isCreateNew: boolean;
        currentTab: statusName;
    }>();

    const emit = defineEmits<{
        (ev: 'showPreview'): void;
        (ev: 'clickedIssueId'): void;
    }>();

    const showStatisticsBtn = computed(() => {
        return currentTab === statusName.scheduled || currentTab === statusName.recurring;
    });

    const disableLink = computed(() => {
        return currentTab === statusName.paused || currentTab === statusName.failed;
    });

    const getHref = computed(() => {
        return isCreateNew || disableLink.value ? 'javascript:void(0)' : '/Issues/' + issue.IssueId;
    });
</script>

<template>
    <h2 class="h4 item-heading">
        <RouterLink
            v-if="currentTab === statusName.sent && !isCreateNew"
            :to="{ name: 'IssueStatistics', params: { issueId: issue.IssueId } }"
            @click="$emit('clickedIssueId')">
            <IssueSubject :subject="issue.Subject" :dynamic-subject="issue.DynamicSubject" :event-name="issue.Event?.Name" />
        </RouterLink>
        <a v-else :role="isCreateNew || disableLink ? 'button' : 'link'" :href="getHref" :class="{ disabled: disableLink }" @click="$emit('clickedIssueId')">
            <IssueSubject :subject="issue.Subject" :dynamic-subject="issue.DynamicSubject" :event-name="issue.Event?.Name" />
        </a>

        <small>{{ issue.Category.Name }}</small>

        <span v-if="issue.UseIssueVariationTest">
            <span class="mini-label mini-label-info">{{ $Resources.IssueVariationsHeading }}</span>
        </span>

        <span v-if="issue.Event">
            <a
                class="mini-label mini-label-info"
                rel="noopener"
                target="_blank"
                :href="'/Events/' + issue.Event.EventId"
                v-tooltip.top="issue.Event.Name || $Resources.NoTitlePlaceholder">
                {{ $Resources.AssociatedEventLabel }}
            </a>
        </span>

        <span v-if="issue.Survey">
            <a
                class="mini-label mini-label-info"
                rel="noopener"
                target="_blank"
                :href="'/Surveys/' + issue.Survey.SurveyId"
                v-tooltip.top="issue.Survey.Title || $Resources.NoTitlePlaceholder">
                {{ $Resources.AssociatedSurveyLabel }}
            </a>
        </span>

        <span v-if="issue.Journey">
            <a
                class="mini-label mini-label-info"
                rel="noopener"
                target="_blank"
                :href="'/journeys/' + issue.Journey.JourneyId"
                v-tooltip.top="issue.Journey.Title || $Resources.NoTitlePlaceholder">
                {{ $Resources.AssociatedJourneyLabel }}
            </a>
        </span>

        <template v-if="!isCreateNew">
            <button type="button" class="btn btn-subtle btn-sm" v-tooltip.top="$Resources.IssuePreviewButton" @click="$emit('showPreview')">
                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                <span class="sr-only">{{ $Resources.IssuePreviewButton }}</span>
            </button>

            <a class="btn btn-default btn-xs m-0-b m-xs-l" :href="'/Issues/' + issue.IssueId + '/Statistics'" v-if="showStatisticsBtn">
                {{ $Resources.IssuesViewStatistic }}
            </a>
        </template>
    </h2>
</template>
